/**
 * Voice mode: talk, and the reply talks back.
 *
 * The microphone streams to `/listen` in converse mode, where Deepgram Flux
 * says when a turn starts and ends. An ended turn is sent as an ordinary
 * message with `voice`, so it lands in the chat like any other and the reply
 * is written to be heard; the reply's words are cut into sentences as they
 * stream and each is said through `/speak` as soon as it is whole. Talking
 * over the reply stops it — unless what the microphone heard is the reply
 * itself, played back through a speaker (audio/echo.ts).
 *
 * The rules are audio/voiceMachine.ts; this carries out what they say.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { speak, type Attachment, type ChatSummary } from '../../api';
import { isEcho } from '../../audio/echo';
import { listen, type Listening } from '../../audio/listen';
import { SentenceSplitter } from '../../audio/sentences';
import { Speaker } from '../../audio/speaker';
import { voiceStep, VOICE_START, type VoiceEffect, type VoiceEvent, type VoiceState } from '../../audio/voiceMachine';
import { startTurn, stopTurn, useLiveTurn, type TurnApp } from '../../hooks/useTurns';
import { Ph } from '../ui';

/** How much of what the voice said is kept to tell echo from the person. */
const SPOKEN_KEPT = 4;

type Step = { state: VoiceState; effects: VoiceEffect[] };

const reducer = (step: Step, e: VoiceEvent): Step => {
  const [state, effects] = voiceStep(step.state, e);
  return { state, effects };
};

const STATUS: Record<VoiceState['phase'], string> = {
  connecting: 'Starting',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking',
  ended: ''
};

export function VoiceMode({
  chat,
  app,
  context,
  takeAttachments,
  onClose
}: {
  chat: ChatSummary;
  app: TurnApp;
  /** Started inside the click that opened voice mode, so it may play. */
  context: AudioContext;
  /** Files waiting to go with the next thing said: the assistant's screen. */
  takeAttachments?: () => Attachment[];
  onClose: (spoke: boolean) => void;
}) {
  const [{ state, effects }, dispatch] = useReducer(reducer, { state: VOICE_START, effects: [] });
  const [level, setLevel] = useState(0);
  const mic = useRef<Listening | null>(null);
  const speaker = useRef<Speaker | null>(null);
  const spoken = useRef<string[]>([]);
  const phase = useRef(state.phase);
  phase.current = state.phase;
  const spoke = useRef(false);
  const micLevel = useRef(0);

  // ── the effects the rules ask for ─────────────────────────────────────────
  const splitter = useRef(new SentenceSplitter());
  const said = useRef(0);
  /** The newest turn the page has shown, and the newest that is not the
   *  reply to what was just said: only a turn after `floor` is spoken. */
  const lastSeq = useRef(-1);
  const floor = useRef(Infinity);
  const doneSeq = useRef(-1);
  useEffect(() => {
    for (const fx of effects) {
      // Whatever is streaming now is not what will be said next.
      floor.current = lastSeq.current;
      splitter.current = new SentenceSplitter();
      said.current = 0;
      if (fx.type === 'interrupt') {
        speaker.current?.stop();
        // Talked over, not stopped: what is said next takes its place.
        stopTurn(chat.id, true);
      } else {
        spoke.current = true;
        startTurn({ chat, kind: 'turn', input: { text: fx.text, attachments: takeAttachments?.() ?? [], voice: true }, app });
      }
    }
    // Each step's effects are carried out once.
  }, [effects]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── the reply, sentence by sentence ───────────────────────────────────────
  const live = useLiveTurn(chat.id);
  useEffect(() => {
    if (!live) return;
    lastSeq.current = Math.max(lastSeq.current, live.seq);
    if (live.seq <= floor.current || doneSeq.current === live.seq) return;
    const fresh = live.text.slice(said.current);
    said.current = live.text.length;
    if (fresh) {
      dispatch({ type: 'reply', text: fresh });
      for (const piece of splitter.current.push(fresh)) speaker.current?.say(piece);
    }
    if (live.phase === 'done' || live.phase === 'stopped') {
      doneSeq.current = live.seq;
      for (const piece of splitter.current.flush()) speaker.current?.say(piece);
      dispatch({ type: 'turnDone' });
      if (!speaker.current?.busy) dispatch({ type: 'speakerIdle' });
    } else if (live.phase === 'failed') {
      doneSeq.current = live.seq;
      dispatch({ type: 'turnFailed', message: live.failure?.message ?? 'That did not work.' });
    }
  }, [live]);

  // ── the microphone and the voice ──────────────────────────────────────────
  useEffect(() => {
    let gone = false;
    speaker.current = new Speaker(context, (text, signal) => speak(text, signal), {
      onSay: (text) => {
        spoken.current = [...spoken.current.slice(-(SPOKEN_KEPT - 1)), text];
        dispatch({ type: 'saying' });
      },
      onIdle: () => dispatch({ type: 'speakerIdle' }),
      onError: () => dispatch({ type: 'turnFailed', message: 'The voice could not play.' })
    });
    listen(
      'converse',
      {
        onEvent: (e) => {
          if (e.type === 'ready') dispatch({ type: 'opened' });
          else if (e.type === 'turn') {
            const busy = phase.current === 'thinking' || phase.current === 'speaking';
            dispatch({ type: 'heard', phase: e.phase, text: e.text, echo: busy && isEcho(e.text, spoken.current) });
          } else if (e.type === 'error') dispatch({ type: 'failed', message: e.message });
        },
        onLevel: (l) => {
          micLevel.current = l;
        },
        onClosed: () => {
          if (!gone) dispatch({ type: 'failed', message: 'Listening stopped.' });
        }
      },
      context
    )
      .then((m) => {
        if (gone) m.close();
        else mic.current = m;
      })
      .catch(() => dispatch({ type: 'failed', message: 'The microphone is not available.' }));

    return () => {
      gone = true;
      mic.current?.close();
      mic.current = null;
      speaker.current?.stop();
      // The context is the opener's to close: StrictMode runs this twice.
    };
  }, [context]);

  useEffect(() => {
    mic.current?.setMuted(state.muted);
  }, [state.muted]);

  // The orb follows whoever is talking.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const voice = speaker.current?.level() ?? 0;
      setLevel((l) => l * 0.7 + Math.max(voice, micLevel.current) * 0.3);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Awake while talking; gone when the page is.
  useEffect(() => {
    let lock: { release(): Promise<void> } | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
    nav.wakeLock?.request('screen').then((l) => (lock = l), () => undefined);
    const onHidden = () => {
      if (document.visibilityState === 'hidden') dispatch({ type: 'end' });
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      void lock?.release().catch(() => undefined);
    };
  }, []);

  const end = useCallback(() => dispatch({ type: 'end' }), []);
  useEffect(() => {
    if (state.phase === 'ended' && !state.error) onClose(spoke.current);
  }, [state.phase, state.error, onClose]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') end();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [end]);

  const caption = state.phase === 'speaking' || (state.phase === 'thinking' && state.reply) ? state.reply : state.you;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Voice"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 30,
        background: 'var(--app-ground)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 20px max(24px, env(safe-area-inset-bottom))'
      }}
    >
      <div style={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', width: '100%' }}>
        <div
          aria-hidden="true"
          className="voice-orb"
          data-phase={state.phase}
          style={{ transform: `scale(${1 + Math.min(level, 1) * 0.35})` }}
        />
      </div>

      <div style={{ width: '100%', maxWidth: 560, minHeight: 96, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span role="status" style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {state.error ?? (state.muted ? 'Muted' : STATUS[state.phase])}
        </span>
        <p
          aria-live="polite"
          style={{
            margin: 0,
            fontSize: 17,
            lineHeight: 1.5,
            color: state.phase === 'listening' ? 'var(--text-muted)' : 'var(--text-strong)',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {caption}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
        {state.phase !== 'ended' ? (
          <button
            type="button"
            className="bare hov-soft"
            aria-label={state.muted ? 'Unmute' : 'Mute'}
            aria-pressed={state.muted}
            onClick={() => dispatch({ type: 'mute', muted: !state.muted })}
            style={roundButton(state.muted ? 'var(--primary-soft)' : 'var(--surface-card)', state.muted ? 'var(--primary)' : 'var(--text-strong)')}
          >
            <Ph name={state.muted ? 'microphone-slash' : 'microphone'} size={22} />
          </button>
        ) : null}
        <button
          type="button"
          className="bare"
          aria-label="End voice"
          onClick={() => (state.phase === 'ended' ? onClose(spoke.current) : end())}
          style={roundButton('var(--danger-500)', '#fff')}
        >
          <Ph name="x" size={22} />
        </button>
      </div>
    </div>
  );
}

const roundButton = (background: string, color: string) => ({
  width: 56,
  height: 56,
  borderRadius: 999,
  display: 'grid',
  placeItems: 'center',
  background,
  color,
  border: '1px solid var(--border-subtle)',
  boxShadow: 'var(--shadow-sm)'
});
